import {
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  ButtonText,
  ButtonIcon
} from '@morressier/alembic';
import { Box, Text, Flex, Tooltip } from '@chakra-ui/react';
import { IPeerReviewListItem } from '@publishing-engine/domain/views';
import {
  recommendationBadges,
  reviewStatusBadges
} from 'src/components/Review/Badges';
import { formatDateWithoutTimeUTC, timeFormatDeadlineDate } from 'src/utils/date';
import {
  RATING_OPTIONS,
  reviewInProgressSubmissionStatuses
} from 'src/utils/reviews';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import {
  IReviewer,
  IPeerReviewReinviteState,
  IPeerReviewWithdrawActions
} from '@publishing-engine/domain/models';
import { URGENCY_THRESHOLD } from '@publishing-engine/domain/fixtures/PeerReview';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import ProtectedReviewWorkflowAction from 'src/components/ProtectedAction/ProtectedReviewWorkflowAction';
import { AdHocModalOpen } from '../AuthorsTab/ComposeEmailModal';

export const renderReviewStatusColumn = (
  review: IPeerReviewListItem,
  onDeclineReviewReason?: (reviewId: string) => void
) => {
  const deadlineExtensionRequested =
    review?.workflow?.deadlineExtensions?.status === 'requested';
  const currentState = deadlineExtensionRequested
    ? 'extensionRequested'
    : review?.workflow?.currentState;

  const declinedReviewReason =
    review?.caseData?.additionalInformation?.declineReason;
  const recommendedReviewers =
    review?.caseData?.additionalInformation?.recommendedReviewers;

  const hasAdditionalReviewInfo =
    declinedReviewReason ||
    (recommendedReviewers && recommendedReviewers.length > 0);

  const currentStateIncludingDeclinedWithReason =
    declinedReviewReason && currentState === 'declined'
      ? 'declinedWithReason'
      : currentState;

  const statusBadge = currentState
    ? reviewStatusBadges[currentStateIncludingDeclinedWithReason]
    : reviewStatusBadges.invited;

  if (currentState === 'declined' && hasAdditionalReviewInfo) {
    return (
      <Box width="100%" overflow="hidden">
        <Flex alignItems="center" gap={2}>
          <Box minWidth={0} overflow="hidden">
            {statusBadge}
          </Box>

          <Tooltip
            shouldWrapChildren
            hasArrow
            label="This Reviewer has sent information about this decision."
            placement="top"
          >
            <Box
              cursor="pointer"
              onClick={e => {
                e.stopPropagation();
                onDeclineReviewReason?.(review.id);
              }}
              flexShrink={0}
            >
              <Badge size="context" variant="caution" alignItems="center">
                <FontAwesomeIcon icon={faInfoCircle} />
              </Badge>
            </Box>
          </Tooltip>
        </Flex>
      </Box>
    );
  }

  return (
    <Box width="100%" overflow="hidden">
      {statusBadge}
    </Box>
  );
};

const REVIEW_INVITE_ACTIONS = [
  'ReviewRequested#manual',
  'ReviewRequested#automated',
  'ReviewRequested#manualBoardMember',
  'ReviewRequested#automaticBoardMember',
  'ReviewInRevisionRequested#manual',
  'ReviewInRevisionRequested#automated',
  'ReviewInRevisionRequested#manualBoardMember',
  'ReviewInRevisionRequested#automaticBoardMember',
  'RevisionReviewRequested',
  'DeclinedReviewInvitationResent',
  'ExpiredReviewInvitationResent',
  'WithdrawnReviewInvitationResent#invited',
  'WithdrawnReviewInvitationResent#assessment'
];

const getInvitedDate = (review: IPeerReviewListItem): string | undefined =>
  review.timeline?.invitedDate ??
  review.workflow.states?.find(
    state =>
      state.completedWith?.action &&
      REVIEW_INVITE_ACTIONS.includes(state.completedWith.action)
  )?.completedWith?.date;

export const renderDeadlineColumn = (review: IPeerReviewListItem) => {
  const { currentState } = review.workflow;
  const invitedDate = getInvitedDate(review);
  const invitedDateText = invitedDate
    ? formatDateWithoutTimeUTC(new Date(invitedDate))
    : 'N/A';
  let isUrgent: boolean | null = null;
  let deadlineDate: Date | null = null;
  let deadlineText = '';

  if (review.workflow.currentDeadline) {
    deadlineDate = new Date(review.workflow.currentDeadline);
    const daysToDeadline = deadlineDate
      ? calculateDaysToDeadline(deadlineDate)
      : null;

    isUrgent =
      typeof daysToDeadline === 'number' && daysToDeadline <= URGENCY_THRESHOLD;

    if (daysToDeadline && daysToDeadline >= 0) {
      if (currentState === 'invited') {
        deadlineText = `${daysToDeadline} days to accept the invitation`;
      } else if (reviewInProgressSubmissionStatuses.includes(currentState)) {
        deadlineText = `${daysToDeadline} days to submit their Review`;
      }
    } else {
      deadlineText = daysToDeadline
        ? `${Math.abs(daysToDeadline)} days overdue`
        : typeof daysToDeadline === 'number' && daysToDeadline === 0
        ? 'Today is the deadline'
        : '';
    }
  }

  return (
    <Flex direction="column" gap={1} textStyle="textContext">
      <Flex gap={1} alignItems="center">
        <Text textStyle="textContext" fontWeight="bold" fontSize={11}>
          On:
        </Text>
        <Text textStyle="textBody" fontSize={11}>
          {invitedDateText}
        </Text>

        {invitedDate && (
          <Tooltip
            label={`Invitation sent On: ${timeFormatDeadlineDate(
              new Date(invitedDate)
            )}`}
            aria-label="Invitation Info"
            placement="top"
            hasArrow
            shouldWrapChildren
          >
            <ButtonIcon
              size="small"
              ariaLabel="Invitation Info Icon"
              icon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          </Tooltip>
        )}
      </Flex>

      <Flex
        gap={1}
        alignItems="center"
        color={isUrgent ? 'actionSecondaryCautionContent' : ''}
      >
        <Text textStyle="textContext" fontWeight="bold" fontSize={11}>
          Due:
        </Text>
        <Text textStyle="textBody" fontSize={11}>
          {deadlineDate ? `${formatDateWithoutTimeUTC(deadlineDate)}` : 'N/A'}
        </Text>

        {deadlineDate && (
          <Tooltip
            label={
              <Text>
                {deadlineText}
                <br /> Due: {timeFormatDeadlineDate(deadlineDate)}
              </Text>
            }
            aria-label="Deadline Info"
            placement="top"
            hasArrow
            shouldWrapChildren
          >
            <ButtonIcon
              size="small"
              ariaLabel="Deadline Info Icon"
              icon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};

export const renderRecommendationColumn = (review: IPeerReviewListItem) => {
  if (review.workflow.currentState === 'inReview') {
    return recommendationBadges.awaitingReview;
  }

  if (
    review.workflow.currentState === 'declined' ||
    !review.caseData.firstAssessment?.recommendation
  ) {
    return 'N/A';
  }

  return (
    <Box paddingRight={1}>
      {recommendationBadges[review.caseData.firstAssessment.recommendation]}
    </Box>
  );
};

export const renderScoreBadge = (scoreKey: string, justification: any) => {
  const score = RATING_OPTIONS[scoreKey];
  return score ? (
    <Badge variant="neutral" size="context" isTruncated>
      <Tooltip label={`${justification || 'N/A'}`} placement="bottom" hasArrow>
        <Box as="span" display="inline-block">
          {`${score.value} (${score.label})`}
        </Box>
      </Tooltip>
    </Badge>
  ) : (
    <Badge variant="neutral" size="context" isTruncated>
      Awaiting
    </Badge>
  );
};

export const renderReviewScoreColumn = (review: IPeerReviewListItem) => {
  const { reviewScore } = review.caseData.firstAssessment || {};
  const { currentState } = review.workflow;

  if (reviewScore?.score) {
    return renderScoreBadge(reviewScore.score, reviewScore.justification);
  }

  switch (currentState) {
    case 'invited':
    case 'declined':
    case 'expired':
    case 'withdrawn':
      return 'N/A';

    case 'rescinded':
    case 'firstAssessment':
    case 'revisionInvited':
    case 'revisionAssessment':
      return recommendationBadges.awaitingReview;

    case 'submittedFirstAssessment':
    case 'submittedRevisionAssessment':
    case 'reviewed':
    default:
      return (
        <Badge variant="neutral" size="context">
          Awaiting
        </Badge>
      );
  }
};

export const renderMenuItem = (
  label: string,
  onClick: () => void,
  textColor?: string,
  disabled?: boolean
) => {
  return (
    <Box onClick={event => event.stopPropagation()}>
      {disabled ? (
        <MenuItem disabled={disabled} ariaLabel={label} onClick={onClick}>
          <Text color={textColor}>{label}</Text>
        </MenuItem>
      ) : (
        <MenuItem ariaLabel={label} onClick={onClick}>
          <Text color={textColor}>{label}</Text>
        </MenuItem>
      )}
    </Box>
  );
};

export const renderActionsColumn = (
  review: IPeerReviewListItem,
  onSelectReviewId: (reviewId: string) => void,
  onSelectedAuthor: (author: IReviewer | null) => void,
  onRateReview: (reviewId: string) => void,
  onRescindReview: (reviewId: string) => void,
  onDeclineReviewReason: (reviewId: string) => void,
  onGrantExtension: (reviewId: string) => void,
  onResendInvite: (
    reviewId: string,
    reviewer: IReviewer,
    currentState: IPeerReviewReinviteState
  ) => void,
  onWithdrawReviewer: (
    reviewId: string,
    reviewer: IReviewer,
    actionName: IPeerReviewWithdrawActions
  ) => void,
  setOpenSendEmailModal: (data: AdHocModalOpen) => void,
  isUserDeactivated?: boolean,
  onReassignBoardMember?: (reviewId: string) => void
) => {
  const { availableReviewActions, isTaskCompleter } = useSubmissionData();
  const { currentState } = review.workflow;
  const canExtendDeadline =
    currentState === 'rescinded' ||
    availableReviewActions.contains(review, 'ExtendReviewDeadline');

  const deadlineExtensionRequested =
    review?.workflow?.deadlineExtensions?.status === 'requested';

  // to show "View Review" menu item only after review is submitted
  const statusAfterReview = [
    'reviewed',
    'rescinded',
    'submittedFirstAssessment',
    'submittedRevisionAssessment'
  ];

  const subtype =
    review?.workflow.previousState === 'invited' ||
    review?.workflow.previousState === 'revisionInvited'
      ? 'invited'
      : 'assessment';

  const renderItem = (
    label: string,
    onClick: () => void,
    textColor?: string,
    isDisabled?: boolean
  ) => renderMenuItem(label, onClick, textColor, isDisabled);

  const renderWithdrawReviewerMenuItem = (
    actionName: IPeerReviewWithdrawActions,
    isDisabled?: boolean
  ) => {
    return availableReviewActions.contains(review, actionName) ? (
      <Box sx={{ '& > span': { display: 'block', width: '100%' } }}>
        <ProtectedReviewWorkflowAction
          reviewId={review.id}
          actionName={actionName}
        >
          {renderItem(
            'Withdraw Reviewer',
            () => {
              onWithdrawReviewer(review.id, review.reviewer, actionName);
            },
            'actionSecondaryCautionContent',
            isDisabled
          )}
        </ProtectedReviewWorkflowAction>
      </Box>
    ) : null;
  };

  return (
    <Menu ariaLabel="Actions">
      <MenuButton>
        <ButtonText>
          <Flex gap={2} alignItems="center">
            <FontAwesomeIcon size="sm" icon={faBars} />
          </Flex>
        </ButtonText>
      </MenuButton>
      <MenuList>
        {statusAfterReview.includes(currentState) &&
          renderItem('View Review', () => {
            onSelectReviewId(review.id);
          })}

        {renderItem(
          'View Reviewer Profile',
          () => onSelectedAuthor(review.reviewer),
          undefined,
          isUserDeactivated ?? false
        )}

        {isTaskCompleter &&
          renderItem(
            'Send Email',
            () =>
              setOpenSendEmailModal({
                status: true,
                defaultEmail: [review.reviewer.email]
              }),
            undefined,
            isUserDeactivated ?? false
          )}

        {review.caseData?.additionalInformation?.declineReason &&
          renderItem(
            'View Additional Info',
            () => onDeclineReviewReason(review.id),
            undefined,
            isUserDeactivated ?? false
          )}

        {!review.isBoardMember &&
          availableReviewActions.contains(
            review,
            'ResendDeclinedReviewInvitation'
          ) && (
            <Box sx={{ '& > span': { display: 'block', width: '100%' } }}>
              <ProtectedReviewWorkflowAction
                reviewId={review.id}
                actionName="ResendDeclinedReviewInvitation"
              >
                {renderItem(
                  'Resend Invitation',
                  () => {
                    onResendInvite(
                      review.id,
                      review.reviewer,
                      currentState as IPeerReviewReinviteState
                    );
                  },
                  undefined,
                  isUserDeactivated ?? false
                )}
              </ProtectedReviewWorkflowAction>
            </Box>
          )}

        {!review.isBoardMember &&
          availableReviewActions.contains(
            review,
            'ResendExpiredReviewInvitation'
          ) && (
            <Box sx={{ '& > span': { display: 'block', width: '100%' } }}>
              <ProtectedReviewWorkflowAction
                reviewId={review.id}
                actionName="ResendExpiredReviewInvitation"
              >
                {renderItem(
                  'Resend Invitation',
                  () => {
                    onResendInvite(
                      review.id,
                      review.reviewer,
                      currentState as IPeerReviewReinviteState
                    );
                  },
                  undefined,
                  isUserDeactivated ?? false
                )}
              </ProtectedReviewWorkflowAction>
            </Box>
          )}

        {!review.isBoardMember &&
          availableReviewActions.contains(
            review,
            `ResendWithdrawnReviewInvitation#${subtype}`
          ) && (
            <Box sx={{ '& > span': { display: 'block', width: '100%' } }}>
              <ProtectedReviewWorkflowAction
                reviewId={review.id}
                actionName={`ResendWithdrawnReviewInvitation#${subtype}`}
              >
                {renderItem(
                  'Resend Invitation',
                  () => {
                    onResendInvite(
                      review.id,
                      review.reviewer,
                      currentState as IPeerReviewReinviteState
                    );
                  },
                  undefined,
                  isUserDeactivated ?? false
                )}
              </ProtectedReviewWorkflowAction>
            </Box>
          )}

        {review.isBoardMember &&
          onReassignBoardMember &&
          ['declined', 'expired', 'withdrawn'].includes(currentState) && (
            <Box sx={{ '& > span': { display: 'block', width: '100%' } }}>
              {renderItem(
                'Reassign Reviewer',
                () => onReassignBoardMember(review.id),
                undefined,
                isUserDeactivated ?? false
              )}
            </Box>
          )}

        {renderWithdrawReviewerMenuItem(
          'EditorialWithdrawInvitedReviewInvitation',
          isUserDeactivated ?? false
        )}
        {renderWithdrawReviewerMenuItem(
          'EditorialWithdrawAcceptedReviewInvitation',
          isUserDeactivated ?? false
        )}

        {deadlineExtensionRequested
          ? renderItem(
              'Review Extension Request',
              () => {
                onGrantExtension(review.id);
              },
              undefined,
              isUserDeactivated ?? false
            )
          : canExtendDeadline &&
            renderItem(
              'Extend Deadline',
              () => {
                onGrantExtension(review.id);
              },
              undefined,
              isUserDeactivated ?? false
            )}

        {review.canRateReview &&
          renderItem(
            'Rate Review',
            () => onRateReview(review.id),
            undefined,
            isUserDeactivated ?? false
          )}

        {availableReviewActions.contains(review, 'RescindFirstAssessment') &&
          renderItem(
            'Rescind Review',
            () => onRescindReview(review.id),
            undefined,
            isUserDeactivated ?? false
          )}
      </MenuList>
    </Menu>
  );
};
